/**
 * Combined attendance sheets.
 *
 * TDC is sold as separate memberships (one day, two day, three day,
 * unlimited, free trial). Each is its own class, but the children train
 * together, so a coach needs one sheet per site rather than one per
 * membership. The same goes for a school that runs one program on several
 * days. A group is only a way of LOOKING at several classes: every
 * check-in and attendance mark is still saved against the child's own class.
 *
 * Free trials stay off these sheets: the club wants them to list paying
 * members only. A trial class is still on the picker on its own.
 */

const ALL_SUFFIX = " - All memberships";

const nameOf = (list, id) =>
  (Array.isArray(list) ? list : []).find((x) => x.id === id)?.name || "";

const TRIAL = /trial/i;

// Which combined sheet a class belongs on, or null for none.
const groupOf = (cls, programs, areas) => {
  const program = nameOf(programs, cls.program_id).trim();
  // Without the program name TDC cannot be told apart, so group nothing
  if (!program) return null;
  if (TRIAL.test(program) || TRIAL.test(cls.name || "")) return null;
  if (/^tdc\b/i.test(program)) {
    // Footprints is its own sheet wherever it is held
    if (/footprints/i.test(cls.name || "")) return { key: "TDC Footprints", label: "TDC Footprints" };
    const area = nameOf(areas, cls.area_id);
    return area ? { key: `TDC ${area}`, label: `TDC ${area}` } : null;
  }
  // One program run on several days at one school (Tuesdays, Thursdays, both)
  if (!cls.school_id || !cls.school_name) return null;
  return { key: `${cls.school_id}:${cls.program_id}`, label: cls.school_name };
};

export const buildClassGroups = (classes = [], programs = [], areas = []) => {
  const groups = new Map();
  for (const cls of classes) {
    const group = groupOf(cls, programs, areas);
    if (!group) continue;
    if (!groups.has(group.key)) {
      groups.set(group.key, {
        id: `group:${group.key}`,
        name: `${group.label}${ALL_SUFFIX}`,
        isGroup: true,
        classes: [],
      });
    }
    groups.get(group.key).classes.push(cls);
  }
  return [...groups.values()]
    .filter((group) => group.classes.length > 1)
    .sort((a, b) => a.name.localeCompare(b.name));
};

const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

// "YYYY-MM-DD" read as a local date: new Date("2026-10-13") is UTC midnight,
// which is the day before anywhere west of Greenwich.
const weekdayOf = (date) => {
  const [y, m, d] = String(date).split("-").map(Number);
  return WEEKDAYS[new Date(y, m - 1, d).getDay()];
};

/**
 * The classes behind a picker choice: a group's members, or the class itself.
 * Given a date, a group keeps only the classes that meet that weekday, so a
 * Thursday-only child is not marked absent on a Tuesday. A class with no
 * days on record is kept, and on a day none of them meet (a make-up session)
 * the whole group is shown rather than an empty sheet.
 */
export const classesOf = (selection, date) => {
  if (!selection) return [];
  if (!selection.isGroup) return [selection];
  if (!date) return selection.classes;
  const weekday = weekdayOf(date);
  const meets = (cls) =>
    !Array.isArray(cls.weekdays) ||
    cls.weekdays.length === 0 ||
    cls.weekdays.some((d) => String(d).toLowerCase() === weekday);
  const scheduled = selection.classes.filter(
    (cls) => Array.isArray(cls.weekdays) && cls.weekdays.length > 0,
  );
  if (scheduled.length && !scheduled.some(meets)) return selection.classes;
  return selection.classes.filter(meets);
};
